import { useState, useEffect } from "react";
import "./AdvancedMathQuiz.css";

export default function AdvancedMathQuiz() {
  const difficulties = [
    { name: "Easy", maxNum: 10, time: 30, multiplier: 1 },
    { name: "Medium", maxNum: 20, time: 25, multiplier: 1.5 },
    { name: "Hard", maxNum: 50, time: 20, multiplier: 2 },
    { name: "Expert", maxNum: 100, time: 15, multiplier: 3 },
    { name: "Challenge", maxNum: 200, time: 10, multiplier: 5 },
  ];

  const [level, setLevel] = useState(0);
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [operator, setOperator] = useState("+");
  const [userAnswer, setUserAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [message, setMessage] = useState("");
  const [timeLeft, setTimeLeft] = useState(difficulties[0].time);
  const [gameOver, setGameOver] = useState(false);

  const [showRules, setShowRules] = useState(false);
  const [showTips, setShowTips] = useState(false);

  // SEO-friendly description
  const pageDescription = "Advanced Math Quiz Challenge: Improve your math skills with timed questions, combo multipliers, and increasing difficulty levels.";

  useEffect(() => {
    document.title = "Advanced Math Quiz Challenge | Fun Math Game";
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.setAttribute("content", pageDescription);
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = pageDescription;
      document.head.appendChild(meta);
    }
  }, []);

  const generateQuestion = () => {
    const max = difficulties[level].maxNum;
    const a = Math.floor(Math.random() * max) + 1;
    const b = Math.floor(Math.random() * max) + 1;
    const ops = ["+", "-", "*", "/", "%", "**"];
    const op = ops[Math.floor(Math.random() * ops.length)];
    setNum1(a);
    setNum2(b === 0 && (op === "/" || op === "%") ? 1 : b);
    setOperator(op);
    setUserAnswer("");
  };

  // Timer countdown
  useEffect(() => {
    if (gameOver) return;
    if (timeLeft <= 0) {
      setGameOver(true);
      return;
    }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, gameOver]);

  useEffect(() => generateQuestion(), [level]);

  const checkAnswer = () => {
    let correctAnswer;
    switch (operator) {
      case "+": correctAnswer = num1 + num2; break;
      case "-": correctAnswer = num1 - num2; break;
      case "*": correctAnswer = num1 * num2; break;
      case "/": correctAnswer = Math.floor(num1 / num2); break;
      case "%": correctAnswer = num1 % num2; break;
      case "**": correctAnswer = num1 ** 2; break;
      default: correctAnswer = 0;
    }

    if (parseInt(userAnswer) === correctAnswer) {
      const newCombo = combo + 1;
      setCombo(newCombo);
      setScore(score + Math.floor(10 * difficulties[level].multiplier * newCombo));
      setMessage(`✅ Correct! Combo x${newCombo}`);
      if (score > 0 && score % 100 === 0 && level < difficulties.length - 1) {
        setLevel(level + 1);
        setMessage(`🎉 Level Up! Now: ${difficulties[level + 1].name}`);
        setTimeLeft(difficulties[level + 1].time);
      }
    } else {
      setCombo(0);
      setMessage(`❌ Wrong! Correct answer: ${correctAnswer}`);
      setTimeLeft(timeLeft - 2); 
    }
    generateQuestion();
  };

  const resetGame = () => {
    setScore(0);
    setCombo(0);
    setLevel(0);
    setTimeLeft(difficulties[0].time);
    setGameOver(false);
    setMessage("");
    generateQuestion();
  };

  return (
    <main className="math-quiz-wrapper">
      <h1>Advanced Math Quiz Challenge</h1>
      <p className="status">
        Level: {difficulties[level].name} | Score: {score} | Combo: x{combo} | Time: {timeLeft}s
      </p>

      <section className="info-buttons">
        <button onClick={() => setShowRules(!showRules)}>📜 Game Rules</button>
        <button onClick={() => setShowTips(!showTips)}>💡 Tips</button>
      </section>

      {showRules && (
        <section className="extra-info">
          <h2>Game Rules</h2>
          <ul>
            <li>Solve the math question before time runs out.</li>
            <li>Correct answers increase score and combo multiplier.</li>
            <li>Wrong answers reset combo and reduce time.</li>
            <li>Levels get harder as you score more points.</li>
            <li>Power, modulo, and division operators included for variety.</li>
          </ul>
        </section>
      )}

      {showTips && (
        <section className="extra-info">
          <h2>Tips to Score Higher</h2>
          <ul>
            <li>Focus on the current question and answer quickly.</li>
            <li>Use mental shortcuts for multiplication and division.</li>
            <li>Combo multiplier increases points fast; aim for streaks.</li>
            <li>Practice makes perfect! Start with Easy and work up.</li>
          </ul>
        </section>
      )}

      {!gameOver ? (
        <section className="quiz-container">
          <p className="question">
            {num1} {operator}{operator === "**" ? "" : ` ${num2}`} = ?
          </p>
          <input
            type="number"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder="Enter your answer"
            aria-label="Your Answer"
          />
          <button onClick={checkAnswer}>Submit</button>
          <p className="message">{message}</p>
        </section>
      ) : (
        <section className="game-over">
          <h2>⏰ Time's Up!</h2>
          <p>Your final score: {score}</p>
          <button onClick={resetGame}>Play Again</button>
        </section>
      )}
    </main>
  );
}
